import { Info } from "lucide-react";

// The six AI specialists, presented as a firm. Each has a focused role
// and hands work to the next; the copy is in each one's own voice. They
// are systems, not people — the closing note makes that explicit.
interface Specialist {
  initial: string;
  avatarBg: string;
  name: string;
  role: string;
  quote: string;
}

const SPECIALISTS: Specialist[] = [
  {
    initial: "D",
    avatarBg: "#2C2C2A",
    name: "Detective",
    role: "Reads the denial",
    quote:
      "I read your insurer's letter twice. I pull out the specific policy section they cited, the claim ID, the deadline, and exactly which clinical criterion they say you missed. Most denials hinge on one sentence; I find it.",
  },
  {
    initial: "R",
    avatarBg: "#444441",
    name: "Researcher",
    role: "Finds the winning policy",
    quote:
      "I look up your plan's actual coverage policy — not a paraphrase — plus CMS, ACR, AMA, and specialty-society guidelines that apply to your condition. I bring back the passages that contradict the denial, with sources.",
  },
  {
    initial: "M",
    avatarBg: "#5F5E5A",
    name: "Medical Records",
    role: "Builds the clinical timeline",
    quote:
      "If you upload your chart, I read it the way a reviewer would. I pull out the dates, the prior treatments tried, the lab values — the evidence that your documentation already meets the criteria your insurer says it doesn't.",
  },
  {
    initial: "L",
    avatarBg: "#1A1A1A",
    name: "Letter Drafter",
    role: "Writes your appeal",
    quote:
      "I write the appeal letter section by section, in the formal register insurers expect. Every clinical claim I write is tied to a real source — never a fabricated citation. You can refine any paragraph before you sign.",
  },
  {
    initial: "C",
    avatarBg: "#8A8A85",
    name: "Citation Checker",
    role: "Verifies every reference",
    quote:
      "Before your letter is finished, I check every cited passage against the actual source — page numbers, quoted text, dates. If something doesn't verify, it doesn't go in. This is the step that separates a real appeal from a fluent one.",
  },
  {
    initial: "S",
    avatarBg: "#9A9A95",
    name: "Submission",
    role: "Packages it to mail",
    quote:
      "I package the final letter — PDF and editable Word — with a payer-specific cover sheet, the right mailing address, and a one-page instruction card. You print, sign, and send. The work is done.",
  },
];

export function SpecialistTeam() {
  return (
    <section className="mx-auto max-w-[1180px] px-6 py-28 max-[980px]:py-[72px] sm:px-8" aria-label="Your case team">
      <div className="lp-reveal mb-14 grid grid-cols-1 items-end gap-16 max-[980px]:gap-10 min-[981px]:grid-cols-[1.2fr_1fr]">
        <div>
          <span className="eyebrow text-brick">The team on your case</span>
          <h2 className="mt-[18px] font-serif text-[44px] font-normal leading-[1.12] tracking-[-0.022em] text-ink max-[980px]:text-[30px]">
            Six AI specialists, working <em className="italic">like a firm</em> would.
          </h2>
        </div>
        <div className="text-[15px] leading-[1.65] text-ink-soft">
          <p>
            An appeal isn&apos;t one task — it&apos;s six different jobs. Each Revetra specialist has a
            focused role and hands work to the next. Below is what each one does, in their own voice.{" "}
            <strong className="font-medium text-ink">They&apos;re AI, not people.</strong> We&apos;re
            naming them so you can see <em className="italic">what</em> is doing what.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-3 border-l-[0.5px] border-t-[0.5px] border-line max-[980px]:grid-cols-2 max-[640px]:grid-cols-1">
        {SPECIALISTS.map((s) => (
          <article
            key={s.name}
            className="lp-reveal relative border-b-[0.5px] border-r-[0.5px] border-line bg-parchment px-7 py-8 transition-colors hover:bg-parchment-soft"
          >
            <header className="mb-4 flex items-center gap-3.5">
              <span
                className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full font-serif text-[18px] font-medium text-parchment"
                style={{ background: s.avatarBg }}
              >
                {s.initial}
              </span>
              <div>
                <h3 className="font-serif text-[18px] font-medium tracking-[-0.005em] text-ink">
                  {s.name}
                </h3>
                <div className="mt-0.5 text-[11px] font-medium uppercase tracking-[0.06em] text-ink-hint">
                  {s.role}
                </div>
              </div>
            </header>
            <p className="lp-specialist-quote border-l-2 border-brick pl-4 font-serif text-[14.5px] italic leading-[1.65] text-ink-soft">
              {s.quote}
            </p>
          </article>
        ))}
      </div>

      <div className="mt-9 flex max-w-[760px] items-start gap-3 rounded-md bg-parchment-soft px-[22px] py-[18px] text-[13px] leading-[1.65] text-ink-muted">
        <Info size={16} className="mt-px flex-shrink-0 text-ink-hint" aria-hidden />
        <div>
          <strong className="font-medium text-ink">About the team being AI.</strong> Every specialist
          above is a Revetra system, not a person. We give them names and voices so you can see
          what&apos;s doing what — and so you can review, refine, or reject any step before your
          appeal is sent.
        </div>
      </div>
    </section>
  );
}
